import React from 'react';
import { BoardMember, LanguageCode } from '../types';
import { translations } from '../i18n/translations';
import { ShieldCheck, Heart, Target, Award, Users, FileText } from 'lucide-react';

interface AboutViewProps {
  currentLang: LanguageCode;
  boardMembers: BoardMember[];
  onNavClick: (tab: string) => void;
}

export const AboutView: React.FC<AboutViewProps> = ({
  currentLang,
  boardMembers,
  onNavClick,
}) => {
  const t = translations[currentLang];

  return (
    <div className="space-y-12 pb-12">
      {/* Page Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <span className="px-3 py-1 rounded-full bg-[#132238] text-[#38BDF8] text-xs font-bold uppercase tracking-wider border border-[#38BDF8]/40 shadow-[0_0_10px_rgba(56,189,248,0.3)]">
          {t.aboutBadge}
        </span>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white font-heading">
          {t.aboutMainTitle}
        </h1>
        <p className="text-sm sm:text-base text-gray-300">
          {t.aboutSubtitle}
        </p>
      </div>

      {/* 1. Founding Story & President's Message */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Founding Story */}
        <div className="glass-card rounded-3xl p-6 sm:p-8 border border-[#38BDF8]/30 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0B1426] text-[#38BDF8] border border-[#38BDF8]/40 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-bold text-white font-heading">{t.foundingStory}</h2>
          </div>
          <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
            {t.foundingStoryP1}
          </p>
          <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
            {t.foundingStoryP2}
          </p>
        </div>

        {/* President Message */}
        <div className="glass-card rounded-3xl p-6 sm:p-8 border border-[#38BDF8]/40 bg-[#132238] space-y-4">
          <div className="flex items-center gap-3">
            <img
              src={boardMembers[0]?.image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&q=80'}
              alt="President"
              className="w-12 h-12 rounded-full object-cover border-2 border-[#38BDF8] shadow-[0_0_12px_rgba(56,189,248,0.5)]"
            />
            <div>
              <h2 className="text-lg font-bold text-white font-heading">{boardMembers[0]?.name}</h2>
              <p className="text-xs text-[#38BDF8] font-medium">{t.presidentsMessage}</p>
            </div>
          </div>
          <blockquote className="text-xs sm:text-sm text-gray-200 italic leading-relaxed border-l-2 border-[#38BDF8] pl-4 py-1">
            {t.presidentsQuote}
          </blockquote>
        </div>
      </div>

      {/* 2. Mission, Vision, Values */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="glass-card rounded-3xl p-6 border border-[#38BDF8]/30 space-y-3">
          <div className="w-10 h-10 rounded-xl bg-[#0B1426] text-[#38BDF8] border border-[#38BDF8]/40 flex items-center justify-center">
            <Target className="w-5 h-5" />
          </div>
          <h3 className="text-lg font-bold text-white">{t.mission}</h3>
          <p className="text-xs text-gray-300 leading-relaxed">
            {t.missionDesc}
          </p>
        </div>

        <div className="glass-card rounded-3xl p-6 border border-[#38BDF8]/30 space-y-3">
          <div className="w-10 h-10 rounded-xl bg-[#0B1426] text-[#38BDF8] border border-[#38BDF8]/40 flex items-center justify-center">
            <Award className="w-5 h-5" />
          </div>
          <h3 className="text-lg font-bold text-white">{t.vision}</h3>
          <p className="text-xs text-gray-300 leading-relaxed">
            {t.visionDesc}
          </p>
        </div>

        <div className="glass-card rounded-3xl p-6 border border-[#38BDF8]/30 space-y-3">
          <div className="w-10 h-10 rounded-xl bg-[#0B1426] text-[#38BDF8] border border-[#38BDF8]/40 flex items-center justify-center">
            <Heart className="w-5 h-5" />
          </div>
          <h3 className="text-lg font-bold text-white">{t.values}</h3>
          <p className="text-xs text-gray-300 leading-relaxed">
            {t.val1Desc}
          </p>
        </div>
      </div>

      {/* 3. Board of Directors */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-[#38BDF8]" />
            <span>{t.boardOfDirectors}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {boardMembers.map((member) => (
            <div
              key={member.id}
              className="glass-card rounded-3xl p-5 text-center border border-[#38BDF8]/30 hover:border-[#38BDF8] hover:shadow-[0_0_20px_rgba(56,189,248,0.4)] transition-all group"
            >
              <img
                src={member.image}
                alt={member.name}
                className="w-24 h-24 rounded-full object-cover mx-auto mb-4 border-2 border-[#38BDF8]/50 group-hover:scale-105 transition-transform"
              />
              <h3 className="text-base font-bold text-white">{member.name}</h3>
              <p className="text-xs text-[#38BDF8] font-semibold mt-1">{member.role}</p>
              <p className="text-[11px] text-gray-300 mt-2 line-clamp-3 leading-relaxed">
                {member.bio}
              </p>
              {member.email && (
                <a
                  href={`mailto:${member.email}`}
                  className="inline-block text-[10px] text-[#7DD3FC] font-medium hover:underline mt-3"
                >
                  {member.email}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 4. Bylaws & Documents CTA */}
      <div className="glass-card rounded-3xl p-6 sm:p-8 border border-[#38BDF8]/40 flex flex-col sm:flex-row items-center justify-between gap-6 bg-[#0B1426]">
        <div className="space-y-1 text-center sm:text-left">
          <h3 className="text-xl font-bold text-white flex items-center justify-center sm:justify-start gap-2">
            <FileText className="w-5 h-5 text-[#38BDF8]" />
            <span>{t.bylaws} & {t.documents}</span>
          </h3>
          <p className="text-xs text-gray-300">
            {t.documentsSubtitle}
          </p>
        </div>

        <button
          onClick={() => onNavClick('documents')}
          className="px-5 py-2.5 rounded-2xl bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_15px_rgba(165,243,252,0.8)] transition-all whitespace-nowrap"
        >
          {t.details}
        </button>
      </div>
    </div>
  );
};
